import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler } from '../api/client.ts';
import { useSession } from '../composants/session.tsx';
import { Entete } from '../composants/mise-en-page.tsx';
import { Carte, Chargement, Erreur, Nombre, Vide } from '../composants/communs.tsx';
import { BoutonImprimer, exporterTableauPdf } from '../composants/ui/index.ts';
import { useApercuPdf } from '../composants/apercu-pdf.tsx';

/**
 * Unités de mesure — permission `unite.gerer`.
 *
 * Pas de désactivation ici, à la différence des catégories : `unite` n'a pas
 * de colonne `actif`. Une unité ne se supprime ni ne se désactive jamais —
 * `produit.unite_base_id` est NOT NULL — seul son libellé se modifie.
 */

interface Unite {
  id: string;
  code: string;
  libelle: string;
  produits: number;
}

export function Unites(): ReactNode {
  const { ouvrir } = useApercuPdf();
  const { peut } = useSession();
  const client = useQueryClient();

  const [code, setCode] = useState('');
  const [libelle, setLibelle] = useState('');
  const [renommee, setRenommee] = useState<{ id: string; libelle: string } | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['unites'],
    queryFn: () => appeler<{ donnees: Unite[] }>('/unites'),
    select: (reponse) => reponse.donnees,
  });

  function rafraichir(): void {
    void client.invalidateQueries({ queryKey: ['unites'] });
    // Le formulaire de produit lit le même référentiel : sans cela, une
    // unité neuve n'apparaîtrait qu'au rechargement de la page.
    void client.invalidateQueries({ queryKey: ['referentiel-catalogue'] });
  }

  const creation = useMutation({
    mutationFn: () =>
      appeler<Unite>('/unites', {
        methode: 'POST',
        corps: { id: crypto.randomUUID(), code, libelle },
      }),
    onSuccess: () => {
      setCode('');
      setLibelle('');
      rafraichir();
    },
  });

  const renommage = useMutation({
    mutationFn: ({ id, valeur }: { id: string; valeur: string }) =>
      appeler<Unite>(`/unites/${id}`, {
        methode: 'PATCH',
        corps: { libelle: valeur },
      }),
    onSuccess: () => {
      setRenommee(null);
      rafraichir();
    },
  });

  const modifiable = peut('unite.gerer');

  function soumettre(evenement: SyntheticEvent): void {
    evenement.preventDefault();
    creation.mutate();
  }

  return (
    <>
      <Entete
        titre="Unités"
        sous="Un produit se compte dans exactement une unité de base."
      />

      <Erreur erreur={error} />
      <Erreur erreur={renommage.error} />

      <div className="pile">
        {modifiable && (
          <Carte titre="Nouvelle unité">
            <form onSubmit={soumettre} className="ligne">
              <div className="champ" style={{ margin: 0, maxWidth: 120 }}>
                <label htmlFor="code-unite">Code</label>
                <input
                  id="code-unite"
                  name="code"
                  required
                  maxLength={10}
                  placeholder="SAC, BID…"
                  value={code}
                  onChange={(evenement) => {
                    setCode(evenement.target.value);
                  }}
                />
              </div>
              <div className="champ" style={{ margin: 0, flex: 1, minWidth: 220 }}>
                <label htmlFor="libelle-unite">Libellé</label>
                <input
                  id="libelle-unite"
                  name="libelle"
                  required
                  minLength={2}
                  placeholder="Sac, Bidon…"
                  value={libelle}
                  onChange={(evenement) => {
                    setLibelle(evenement.target.value);
                  }}
                />
              </div>
              <button type="submit" disabled={creation.isPending}>
                {creation.isPending ? 'Création…' : 'Créer'}
              </button>
            </form>
            <Erreur erreur={creation.error} />
          </Carte>
        )}

        <Carte>
          {isPending ? (
            <Chargement />
          ) : data === undefined || data.length === 0 ? (
            <Vide texte="Aucune unité. Créez-en une avant d’ajouter des produits." />
          ) : (
            <>
              <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Unités',
                        nomFichier: 'unites',
                        colonnes: [
                          { cle: 'code', entete: 'Code', rendu: () => null, texte: (u: Unite) => u.code },
                          {
                            cle: 'libelle',
                            entete: 'Libellé',
                            rendu: () => null,
                            texte: (u: Unite) => u.libelle,
                          },
                          {
                            cle: 'produits',
                            entete: 'Produits',
                            numerique: true,
                            rendu: () => null,
                            texte: (u: Unite) => String(u.produits),
                          },
                        ],
                        lignes: data,
                      }),
                    );
                  }}
                />
              </div>
              <div className="tableau-defilant">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Libellé</th>
                      <th>Produits</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((unite) => (
                      <tr key={unite.id}>
                        <td>{unite.code}</td>
                        <td>
                          {renommee?.id === unite.id ? (
                            <input
                              aria-label={`Renommer ${unite.libelle}`}
                              autoFocus
                              value={renommee.libelle}
                              onChange={(evenement) => {
                                setRenommee({
                                  id: unite.id,
                                  libelle: evenement.target.value,
                                });
                              }}
                              onKeyDown={(evenement) => {
                                if (evenement.key === 'Enter') {
                                  renommage.mutate({
                                    id: unite.id,
                                    valeur: renommee.libelle,
                                  });
                                }
                                if (evenement.key === 'Escape') {
                                  setRenommee(null);
                                }
                              }}
                            />
                          ) : (
                            unite.libelle
                          )}
                        </td>
                        <td>
                          <Nombre valeur={unite.produits} />
                        </td>
                        <td>
                          {modifiable && (
                            <div className="ligne">
                              {renommee?.id === unite.id ? (
                                <button
                                  type="button"
                                  className="bouton--discret"
                                  onClick={() => {
                                    renommage.mutate({
                                      id: unite.id,
                                      valeur: renommee.libelle,
                                    });
                                  }}
                                >
                                  Enregistrer
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="bouton--discret"
                                  onClick={() => {
                                    setRenommee({ id: unite.id, libelle: unite.libelle });
                                  }}
                                >
                                  Renommer
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Carte>
      </div>
    </>
  );
}
